import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'

import InnerOrganizationLayout from 'layouts/InnerOrganizationLayout'

import FullScreenLoading from 'components/FullScreenLoading'
import AlertActivationOrganize from 'components/AlertActivationOrganize'

import useQueryParams from 'hooks/useQueryParams'

import { appLocalOrgTokenRef } from 'utils/localService/app'

import { useOrganizationContext } from 'contexts/OrganizationProvider'

import type { OrganizationStatus } from 'graphQL/schemaType/organization'
import { AppStateType } from 'redux/application/types'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import { replacePath } from 'utils/mapText'
import type { IFrameOrganizationParams } from './types'
import useServiceMenuQuery from 'hooks/useServiceMenuQuery'
import { sortMenu } from 'utils/sortMenu'
import { useTranslation } from 'react-i18next'
import useGenerateOrgTokenRef from 'graphQL/useGenerateOrgTokenRef'
import useGenerateAuthCode from 'graphQL/useGenerateAuthCode'
import { GenerateAuthCodeAPIPayload } from 'graphQL/useGenerateAuthCode/useGenerateAuthCode'
import useGetOrganization from 'graphQL/useGetOrganization'
import usePermission from 'hooks/usePermission'

function IFrameOrganization(): JSX.Element {
  const serviceKey = useQueryParams('serviceKey')
  const service: AppStateType = useSelector((state: RootState) => state.appReducer)
  const organization = useOrganizationContext()

  const [authCodeResp, setAuthCodeResp] = useState<GenerateAuthCodeAPIPayload>()

  const { loading } = useServiceMenuQuery()
  const { i18n } = useTranslation()

  const permission = usePermission('ALL_ORG_MANAGEMENT')

  const history = useHistory()
  if ((!serviceKey || serviceKey === 'undefined') && service.serviceMenus.length > 0) {
    const newServiceKey = sortMenu(service?.serviceMenus)[0]?.service.serviceKey
    history.replace(`${window.location.pathname}?serviceKey=${newServiceKey}`)
  }

  const accountResp = useGetOrganization({
    fetchPolicy: 'network-only',
    variables: {
      input: {
        filter: {
          orgKey: organization.orgKey,
        },
      },
    },
  })
  const generateTokenRefResp = useGenerateOrgTokenRef({
    variables: {
      orgKey: organization.orgKey,
    },
    onCompleted(resp) {
      appLocalOrgTokenRef.set(resp.generateOrgTokenRef.payload.ref)
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const [generateAuthCode, generateAuthCodeResp] = useGenerateAuthCode({
    variables: {
      orgKey: organization.orgKey,
    },
    onCompleted(resp) {
      setAuthCodeResp(resp.generateAuthCode.payload)
    },
    onError(err) {
      console.log(err)
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const params = useParams<IFrameOrganizationParams>()

  useEffect(() => {
    const listenHistory = history.listen(async () => {
      generateTokenRefResp.refetch()
      // await generateAuthCode()
    })

    return () => {
      listenHistory()
    }
  }, [history, generateTokenRefResp, generateAuthCode])

  useEffect(() => {
    generateAuthCode()
  }, [generateAuthCode])

  return (
    <InnerOrganizationLayout>
      {generateAuthCodeResp.loading || generateTokenRefResp.loading || accountResp.loading || loading ? (
        <FullScreenLoading />
      ) : (
        <div className="admin-content-container">
          {!accountResp.loading && permission.canAccess && (
            <AlertActivationOrganize
              status={accountResp.data?.getOrganization.payload[0]?.status as OrganizationStatus}
            />
          )}
          <iframe
            title="Configuration"
            src={replacePath(
              service?.serviceMenus.find((service) => service.service.serviceKey === serviceKey)?.pattern || '',
              {
                endpoint: service?.serviceMenus.find((service) => service.service?.serviceKey === serviceKey)?.service
                  ?.adminPanelEndpoint,
                authorization_code: generateTokenRefResp.data?.generateOrgTokenRef.payload.ref,
                path: params.slug,
                locale: i18n.language === 'enUS' ? 'en' : 'th',
                credentialKey: authCodeResp?.credentialKey,
              }
            )}
            style={{ width: '100%', height: 'calc(100vh - 64px)' }}
            frameBorder="0"
          />
        </div>
      )}
    </InnerOrganizationLayout>
  )
}

export default IFrameOrganization
